/**
 * RUBYTILL PAY — the paired-device panel under a register's Credit row.
 *
 * Shown by RegisterPaymentTypes when the reader bound to the register is a
 * `dejavoo_rubytill_pay` device. Spec: docs/agenticpos-rubytill/rubytill-pay.md
 * (Admin API). It is its own small form with its own Save, because these are
 * the TERMINAL's settings (one row on the EMV device), not the register's —
 * the page's save bar never sends them, and this never sends register fields.
 *
 *   Counter P17 — RubyTill Pay   ● Paired · online · seen 12s ago   [New pairing code] [Unpair]
 *   What it does at this register   Charge only | Customer display
 *   Line on the card screen         settings.displayText
 *   charge_only        → Show Approved for 2s / 3s / 5s
 *   customer_display   → Tips on the P17 · Receipt & thank you
 *
 * Save sends ONLY what changed (mode, settings, pin). `settings` goes as one
 * merged object when any part of it changed, so the server gets a complete,
 * valid settings block whether it merges or replaces.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { CreditCard, Unlink, KeyRound, X, Plus } from 'lucide-react';
import { api } from '../../services/apiService';
import { toastService } from '../../services/toastService';
import { RubyGemMark } from '../RubyGemMark';
import { Switch } from './payment-types/ui';
import { RubyTillPayLiveBlock } from './RubyTillPayLiveBlock';
import {
  RtpayModeCards, RtpayPairingCodePanel, type RtpayRegisterMode, type RtpayPairingCode,
  isValidRtpayPin, toMs, isTapToPayDevice, isValidMerchantCode, ANDROID_TAP_TO_PAY_MODEL,
} from './RubyTillPayRegisterFields';

// ---------------------------------------------------------------------------
// Model (pure — exported for tests)
// ---------------------------------------------------------------------------

export interface RtpayDraft {
  mode: RtpayRegisterMode;
  displayText: string;
  approvedSeconds: number;
  tipEnabled: boolean;
  tipPercents: number[];
  tipAllowCustom: boolean;
  receiptText: boolean;
  receiptEmail: boolean;
  receiptPrint: boolean;
  thankYouMessage: string;
  returnSeconds: number;
  /** Every terminal screen in one theme. Light default: Dejavoo's card screen is white. */
  theme: 'light' | 'dark';
}

export const APPROVED_SECONDS = [2, 3, 5];
export const RETURN_SECONDS = [5, 10, 20];
export const MAX_TIP_PERCENTS = 4;

/** Read the server's `rtpay` block, defaults per RtpaySettingsSchema. */
export function draftFromRtpay(rtpay: any): RtpayDraft {
  const s = rtpay?.settings || {};
  const tip = s.tip || {};
  const rc = s.receipt || {};
  return {
    mode: rtpay?.mode === 'customer_display' ? 'customer_display' : 'charge_only',
    displayText: typeof s.displayText === 'string' ? s.displayText : '',
    approvedSeconds: Number.isFinite(s.approvedSeconds) ? s.approvedSeconds : 3,
    tipEnabled: tip.enabled !== false,
    tipPercents: Array.isArray(tip.percents) ? tip.percents.filter((n: any) => Number.isFinite(n)) : [15, 18, 20],
    tipAllowCustom: tip.allowCustom !== false,
    receiptText: rc.text !== false,
    receiptEmail: rc.email !== false,
    receiptPrint: rc.print === true,
    thankYouMessage: typeof s.thankYouMessage === 'string' ? s.thankYouMessage : '',
    returnSeconds: Number.isFinite(s.returnSeconds) ? s.returnSeconds : 10,
    theme: s.theme === 'dark' ? 'dark' : 'light',
  };
}

const settingsOf = (d: RtpayDraft, base: any) => {
  const out: Record<string, unknown> = {
    ...(base || {}),
    approvedSeconds: d.approvedSeconds,
    tip: { ...(base?.tip || {}), enabled: d.tipEnabled, percents: d.tipPercents, allowCustom: d.tipAllowCustom },
    receipt: { ...(base?.receipt || {}), text: d.receiptText, email: d.receiptEmail, print: d.receiptPrint },
    returnSeconds: d.returnSeconds,
    theme: d.theme,
  };
  // Empty text = unset (the schema's optional), never an empty string on the terminal.
  if (d.displayText.trim()) out.displayText = d.displayText.trim(); else delete out.displayText;
  if (d.thankYouMessage.trim()) out.thankYouMessage = d.thankYouMessage.trim(); else delete out.thankYouMessage;
  return out;
};

/** The PUT body — changed fields only, or null when nothing changed. */
export function buildRtpayPatch(
  base: RtpayDraft, draft: RtpayDraft, baseSettings: any, pin?: string,
): { mode?: RtpayRegisterMode; settings?: Record<string, unknown>; pin?: string } | null {
  const out: { mode?: RtpayRegisterMode; settings?: Record<string, unknown>; pin?: string } = {};
  if (base.mode !== draft.mode) out.mode = draft.mode;
  const { mode: _a, ...bRest } = base;
  const { mode: _b, ...dRest } = draft;
  if (JSON.stringify(bRest) !== JSON.stringify(dRest)) out.settings = settingsOf(draft, baseSettings);
  if (pin && pin.trim()) out.pin = pin.trim();
  return Object.keys(out).length ? out : null;
}

const agoText = (ms: number | null, now: number) => {
  if (!ms) return null;
  const s = Math.max(0, Math.round((now - ms) / 1000));
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

// ---------------------------------------------------------------------------
// Small controls
// ---------------------------------------------------------------------------

const Chip: React.FC<{ on: boolean; onClick: () => void; disabled?: boolean; children: React.ReactNode; testId?: string }> = ({ on, onClick, disabled, children, testId }) => (
  <button
    type="button"
    data-testid={testId}
    aria-pressed={on}
    disabled={disabled}
    onClick={onClick}
    className={`inline-flex min-h-[44px] min-w-[44px] items-center justify-center rounded-full border px-3 text-[13px] font-medium disabled:opacity-50 ${
      on ? 'border-ruby bg-[#FFF1F3] text-ruby-deep' : 'border-stone-200 bg-white text-stone-600 hover:border-stone-300'}`}
  >
    {children}
  </button>
);

const Check: React.FC<{ on: boolean; label: string; onChange: (v: boolean) => void; disabled?: boolean }> = ({ on, label, onChange, disabled }) => (
  <button
    type="button"
    role="checkbox"
    aria-checked={on}
    disabled={disabled}
    onClick={() => onChange(!on)}
    className="inline-flex min-h-[44px] items-center gap-2 rounded-lg pr-3 text-[13px] text-stone-800 disabled:opacity-50"
  >
    <span className={`grid h-5 w-5 place-items-center rounded-md border ${on ? 'border-ruby bg-ruby text-white' : 'border-stone-300 bg-white'}`}>
      {on && <svg viewBox="0 0 12 12" className="h-3 w-3" aria-hidden><path d="M2 6.5 5 9l5-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>}
    </span>
    {label}
  </button>
);

const Field: React.FC<{ label: string; hint?: string; children: React.ReactNode }> = ({ label, hint, children }) => (
  <div className="border-t border-stone-200/70 py-3 first:border-t-0">
    <div className="text-[13px] font-medium text-stone-900">{label}</div>
    {hint && <div className="mt-0.5 text-xs leading-[1.45] text-stone-600">{hint}</div>}
    <div className="mt-2">{children}</div>
  </div>
);

const TEXT = 'min-h-[44px] w-full rounded-lg border border-stone-300 bg-white px-3 text-sm text-stone-900 outline-none placeholder-stone-400 focus:border-ruby focus:ring-2 focus:ring-rose-200 sm:max-w-md';

// ---------------------------------------------------------------------------
// Panel
// ---------------------------------------------------------------------------

export interface RubyTillPayDevicePanelProps {
  tenantId: string;
  device: { deviceId: string; deviceName?: string; model?: string; rtpay?: { cardEngine?: string } | null; rtpayCardEngine?: string };
  disabled?: boolean;
  /** Open the pairing-code panel at once (a device that was just registered). */
  startPairing?: boolean;
}

export const RubyTillPayDevicePanel: React.FC<RubyTillPayDevicePanelProps> = ({ tenantId, device, disabled, startPairing }) => {
  const deviceId = device.deviceId;
  const [data, setData] = useState<any | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [base, setBase] = useState<RtpayDraft | null>(null);
  const [draft, setDraft] = useState<RtpayDraft | null>(null);
  const [pin, setPin] = useState('');
  const [pinOpen, setPinOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [pairing, setPairing] = useState<{ initial: RtpayPairingCode | null } | null>(startPairing ? { initial: null } : null);
  /* Android Tap to Pay only: a new Merchant Code. Never pre-filled — the server
     only ever sends its last 4. */
  const [mc, setMc] = useState('');
  const [mcOpen, setMcOpen] = useState(false);
  const [confirmUnpair, setConfirmUnpair] = useState(false);
  const [unpairing, setUnpairing] = useState(false);
  const [newPct, setNewPct] = useState('');
  const [now, setNow] = useState(() => Date.now());

  /** `keepDraft` re-reads status without discarding unsaved edits. */
  const load = useCallback(async (keepDraft?: boolean) => {
    try {
      const res = await api.posNmi.getRtpay(tenantId, deviceId);
      setData(res);
      setLoadError(null);
      const d = draftFromRtpay(res?.rtpay);
      setBase(d);
      if (!keepDraft) setDraft(d);
    } catch (e: any) {
      setLoadError(e?.serverMessage || e?.message || 'Could not read this terminal’s RubyTill Pay settings.');
    }
  }, [tenantId, deviceId]);

  useEffect(() => { load(); }, [load]);

  // "seen 12s ago" ticks; status refreshes every 15s without touching the draft.
  useEffect(() => {
    const tick = setInterval(() => setNow(Date.now()), 1000);
    const poll = setInterval(() => { load(true); }, 15000);
    return () => { clearInterval(tick); clearInterval(poll); };
  }, [load]);

  const patch = useMemo(
    () => (base && draft ? buildRtpayPatch(base, draft, data?.rtpay?.settings, pinOpen ? pin : '') : null),
    [base, draft, data, pin, pinOpen],
  );
  const pinBad = pinOpen && !!pin && !isValidRtpayPin(pin);
  const mcBad = mcOpen && !!mc && !isValidMerchantCode(mc);
  const mcChange = mcOpen && !!mc && !mcBad ? mc.trim() : null;
  const dirty = (!!patch || !!mcChange) && !pinBad && !mcBad;

  const save = async () => {
    if ((!patch && !mcChange) || pinBad || mcBad) return;
    setSaving(true); setSaveError(null);
    try {
      await api.posNmi.putRtpay(tenantId, deviceId, { ...(patch || {}), ...(mcChange ? { merchantCode: mcChange } : {}) });
      toastService.success(data?.status?.online
        ? 'Saved and sent to the terminal.'
        : 'Saved. The terminal is offline — it picks the settings up when it reconnects.');
      setPin(''); setPinOpen(false); setMc(''); setMcOpen(false);
      await load();
    } catch (e: any) {
      setSaveError(e?.serverMessage || e?.message || 'Could not save these settings.');
    } finally {
      setSaving(false);
    }
  };

  const unpair = async () => {
    setUnpairing(true);
    try {
      await api.posNmi.unpairRtpay(tenantId, deviceId);
      toastService.success(`${device.deviceName || 'The terminal'} unpaired.`);
      setConfirmUnpair(false);
      setPairing(null);
      await load(true);
    } catch (e: any) {
      toastService.error(e?.serverMessage || e?.message || 'Could not unpair this terminal.');
    } finally {
      setUnpairing(false);
    }
  };

  const name = device.deviceName || data?.rtpay?.deviceName || 'Card terminal';
  /* Android Tap to Pay: the same lane, a phone/tablet reading NFC instead of a P-series terminal. */
  const tapToPay = data?.cardEngine === 'tap_to_pay' || isTapToPayDevice(device);
  const product = tapToPay ? ANDROID_TAP_TO_PAY_MODEL : 'RubyTill Pay';
  const merchantLast4: string | null = data?.merchantCodeLast4 || data?.rtpay?.merchantCodeLast4 || null;
  const status = data?.status || {};
  const lastSeen = toMs(status.lastSeenAt);
  const seen = agoText(lastSeen, now);
  const statusLine = !data
    ? null
    : !status.paired
      ? { dot: 'bg-stone-400', text: data?.pairing ? 'Not paired · a code is waiting on the terminal' : 'Not paired' }
      : status.online
        ? { dot: 'bg-green-600', text: `Paired · online${seen ? ` · seen ${seen}` : ''}` }
        : { dot: 'bg-amber-500', text: `Paired · offline${seen ? ` · last seen ${seen}` : ''}` };

  const off = disabled || saving;
  const set = (p: Partial<RtpayDraft>) => setDraft((d) => (d ? { ...d, ...p } : d));

  const addPct = () => {
    const n = parseInt(newPct, 10);
    if (!draft || !Number.isFinite(n) || n < 1 || n > 100) return;
    if (draft.tipPercents.includes(n) || draft.tipPercents.length >= MAX_TIP_PERCENTS) { setNewPct(''); return; }
    set({ tipPercents: [...draft.tipPercents, n].sort((a, b) => a - b) });
    setNewPct('');
  };

  return (
    <div data-testid="rtpay-device-panel" className="border-t border-stone-100 bg-stone-50 py-3 pl-[18px] pr-[18px] sm:pl-[58px]">
      {/* Header — the device and its link */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-stone-900 text-white"><CreditCard size={14} /></span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-stone-900" data-testid="rtpay-device-name">{name === product ? name : `${name} — ${product}`}</div>
          {statusLine ? (
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-stone-600" data-testid="rtpay-status">
              <span className={`h-2 w-2 rounded-full ${statusLine.dot}`} />
              {statusLine.text}
              {status.appVersion ? <span className="text-stone-400"> · app {status.appVersion}</span> : null}
            </div>
          ) : !loadError ? (
            <div className="mt-0.5 flex items-center gap-1.5 text-xs text-stone-500"><RubyGemMark size={12} drawing color="currentColor" /> Reading the terminal…</div>
          ) : null}
        </div>
        <div className="flex flex-wrap gap-2">
          <button type="button" disabled={off} data-testid="rtpay-new-code"
            onClick={() => { setConfirmUnpair(false); setPairing({ initial: null }); }}
            className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-700 hover:border-ruby hover:text-ruby disabled:opacity-50">
            <KeyRound size={13} /> New pairing code
          </button>
          {status.paired && (
            <button type="button" disabled={off} data-testid="rtpay-unpair"
              onClick={() => { setPairing(null); setConfirmUnpair(true); }}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-700 hover:border-red-300 hover:bg-red-50 hover:text-red-700 disabled:opacity-50">
              <Unlink size={13} /> Unpair
            </button>
          )}
        </div>
      </div>

      {loadError && (
        <p className="mt-2 rounded-lg bg-amber-50 px-3 py-2 text-[12px] text-amber-800">
          {loadError}{' '}
          <button type="button" onClick={() => load()} className="min-h-[44px] font-semibold text-ruby hover:text-ruby-deep">Retry</button>
        </p>
      )}

      {/* In-page confirm — never a native confirm() */}
      {confirmUnpair && (
        <div className="mt-3 rounded-xl border border-red-200 bg-white p-3" role="alertdialog" aria-label="Unpair terminal">
          <p className="text-[13px] font-semibold text-stone-900">Unpair {name}?</p>
          <p className="mt-0.5 text-xs leading-[1.45] text-stone-600">
            The terminal signs out of RubyTill Pay at once and this till cannot take a card on it until it is paired again with a new code.
          </p>
          <div className="mt-2 flex gap-2">
            <button type="button" onClick={unpair} disabled={unpairing} data-testid="rtpay-unpair-confirm"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-stone-900 px-4 text-[12px] font-semibold text-white hover:bg-black disabled:opacity-60">
              {unpairing && <RubyGemMark size={13} drawing color="currentColor" />} Unpair
            </button>
            <button type="button" onClick={() => setConfirmUnpair(false)} disabled={unpairing}
              className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-4 text-[12px] font-semibold text-stone-600 hover:bg-stone-50">
              Cancel
            </button>
          </div>
        </div>
      )}

      {pairing && (
        <div className="mt-3">
          <RtpayPairingCodePanel
            tenantId={tenantId}
            deviceId={deviceId}
            initial={pairing.initial}
            autoStart
            onPaired={() => load(true)}
            onClose={() => setPairing(null)}
          />
        </div>
      )}

      {status.paired && <RubyTillPayLiveBlock tenantId={tenantId} emvDeviceId={deviceId} disabled={disabled} />}

      {draft && (
        <div className="mt-3 rounded-xl border border-stone-200 bg-white px-4">
          <Field label="What it does at this register">
            <RtpayModeCards value={draft.mode} onChange={(m) => set({ mode: m })} disabled={off} />
          </Field>

          <Field label="Screen theme" hint="Every screen on the terminal in one theme. Light matches Dejavoo’s white card screen; later this follows the POS theme.">
            <div className="flex flex-wrap gap-1.5">
              {(['light', 'dark'] as const).map((t) => (
                <Chip key={t} on={draft.theme === t} disabled={off} onClick={() => set({ theme: t })} testId={`rtpay-theme-${t}`}>
                  {t === 'light' ? 'Light' : 'Dark'}
                </Chip>
              ))}
            </div>
          </Field>

          <Field label="Line on the card screen" hint="Shown above the amount while the terminal waits for the card. Leave blank for none.">
            <input
              value={draft.displayText}
              maxLength={60}
              disabled={off}
              onChange={(e) => set({ displayText: e.target.value })}
              placeholder="e.g. Thanks for shopping local"
              className={TEXT}
              data-testid="rtpay-display-text"
            />
          </Field>

          {draft.mode === 'charge_only' ? (
            <Field label="Show Approved for" hint="Tip, loyalty and receipt stay on this till’s customer display.">
              <div className="flex flex-wrap gap-1.5">
                {APPROVED_SECONDS.map((s) => (
                  <Chip key={s} on={draft.approvedSeconds === s} disabled={off} onClick={() => set({ approvedSeconds: s })} testId={`rtpay-approved-${s}`}>
                    {s}s
                  </Chip>
                ))}
              </div>
            </Field>
          ) : (
            <>
              <Field label={tapToPay ? 'Tips on the device' : 'Tips on the P17'} hint="The till sends the subtotal; the terminal asks for the tip and sends it back with the sale.">
                <div className="flex items-center gap-2">
                  <Switch on={draft.tipEnabled} label="Tips on the terminal" disabled={off} onChange={(v) => set({ tipEnabled: v })} />
                  <span className="text-[13px] text-stone-700">{draft.tipEnabled ? 'Ask for a tip' : 'No tip prompt'}</span>
                </div>
                {draft.tipEnabled && (
                  <div className="mt-2 space-y-2">
                    <div className="flex flex-wrap items-center gap-1.5" data-testid="rtpay-tip-percents">
                      {draft.tipPercents.map((p) => (
                        <span key={p} className="inline-flex h-11 items-center gap-1 rounded-full border border-ruby bg-[#FFF1F3] pl-3 text-[13px] font-medium text-ruby-deep">
                          {p}%
                          <button type="button" aria-label={`Remove ${p}%`} disabled={off}
                            onClick={() => set({ tipPercents: draft.tipPercents.filter((x) => x !== p) })}
                            className="grid h-11 w-9 place-items-center rounded-full text-ruby-deep hover:text-ruby disabled:opacity-50">
                            <X size={13} />
                          </button>
                        </span>
                      ))}
                      {draft.tipPercents.length < MAX_TIP_PERCENTS && (
                        <span className="inline-flex h-11 items-center rounded-full border border-dashed border-stone-300 bg-white pl-3">
                          <input
                            value={newPct}
                            inputMode="numeric"
                            disabled={off}
                            aria-label="Add a tip percentage"
                            onChange={(e) => setNewPct(e.target.value.replace(/\D/g, '').slice(0, 3))}
                            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPct(); } }}
                            placeholder="%"
                            className="w-10 bg-transparent text-[13px] outline-none"
                          />
                          <button type="button" aria-label="Add percentage" disabled={off || !newPct} onClick={addPct}
                            className="grid h-11 w-10 place-items-center rounded-full text-stone-600 hover:text-ruby disabled:opacity-40">
                            <Plus size={14} />
                          </button>
                        </span>
                      )}
                    </div>
                    <p className="text-[11px] text-stone-500">Up to {MAX_TIP_PERCENTS} choices.</p>
                    <Check on={draft.tipAllowCustom} label="Custom amount" disabled={off} onChange={(v) => set({ tipAllowCustom: v })} />
                  </div>
                )}
              </Field>

              <Field label="Receipt & thank you" hint="What the customer is offered on the terminal after an approved card.">
                <div className="flex flex-wrap gap-x-4">
                  <Check on={draft.receiptText} label="Text" disabled={off} onChange={(v) => set({ receiptText: v })} />
                  <Check on={draft.receiptEmail} label="Email" disabled={off} onChange={(v) => set({ receiptEmail: v })} />
                  <Check on={draft.receiptPrint} label="Print" disabled={off} onChange={(v) => set({ receiptPrint: v })} />
                </div>
                <input
                  value={draft.thankYouMessage}
                  maxLength={60}
                  disabled={off}
                  onChange={(e) => set({ thankYouMessage: e.target.value })}
                  placeholder="Thank-you message, e.g. Thanks — see you soon!"
                  className={`${TEXT} mt-2`}
                  data-testid="rtpay-thank-you"
                />
                <div className="mt-3 text-xs font-medium text-stone-700">Back to start after</div>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  {RETURN_SECONDS.map((s) => (
                    <Chip key={s} on={draft.returnSeconds === s} disabled={off} onClick={() => set({ returnSeconds: s })} testId={`rtpay-return-${s}`}>
                      {s}s
                    </Chip>
                  ))}
                </div>
              </Field>
            </>
          )}

          {tapToPay && (
            <Field
              label="Merchant Code"
              hint={merchantLast4
                ? `Ends in ••${merchantLast4}. Tap to Pay registers with this code and the TPN; a change is sent to the device.`
                : 'Not set — Tap to Pay cannot register on the device without it.'}
            >
              {mcOpen ? (
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    inputMode="numeric"
                    autoComplete="off"
                    value={mc}
                    disabled={off}
                    onChange={(e) => setMc(e.target.value.replace(/\D/g, '').slice(0, 20))}
                    placeholder="Merchant Code, digits"
                    data-testid="rtpay-merchant-code"
                    className={`min-h-[44px] w-56 rounded-lg border bg-white px-3 font-mono text-sm outline-none focus:border-ruby focus:ring-2 focus:ring-rose-200 ${mcBad ? 'border-red-300 bg-red-50' : 'border-stone-300'}`}
                  />
                  <button type="button" onClick={() => { setMc(''); setMcOpen(false); }}
                    className="min-h-[44px] rounded-lg px-3 text-[12px] font-semibold text-stone-600 hover:text-stone-900">
                    Keep current
                  </button>
                  {mcBad && <span className="w-full text-[11px] text-red-700">Digits only, 6 to 20 long.</span>}
                </div>
              ) : (
                <button type="button" disabled={off} onClick={() => setMcOpen(true)} data-testid="rtpay-merchant-code-change"
                  className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-700 hover:border-ruby hover:text-ruby disabled:opacity-50">
                  {merchantLast4 ? 'Change Merchant Code' : 'Set Merchant Code'}
                </button>
              )}
            </Field>
          )}

          <Field label="Terminal PIN" hint={data?.rtpay?.hasPin ? 'Set. Opens the staff menu on the terminal.' : 'Not set — anyone at the counter can open the staff menu.'}>
            {pinOpen ? (
              <div className="flex flex-wrap items-center gap-2">
                <input
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  value={pin}
                  disabled={off}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="New PIN, 4–6 digits"
                  className={`min-h-[44px] w-48 rounded-lg border bg-white px-3 font-mono text-sm tracking-widest outline-none focus:border-ruby focus:ring-2 focus:ring-rose-200 ${pinBad ? 'border-red-300 bg-red-50' : 'border-stone-300'}`}
                />
                <button type="button" onClick={() => { setPin(''); setPinOpen(false); }}
                  className="min-h-[44px] rounded-lg px-3 text-[12px] font-semibold text-stone-600 hover:text-stone-900">
                  Keep current
                </button>
              </div>
            ) : (
              <button type="button" disabled={off} onClick={() => setPinOpen(true)}
                className="min-h-[44px] rounded-lg border border-stone-300 bg-white px-3 text-[12px] font-semibold text-stone-700 hover:border-ruby hover:text-ruby disabled:opacity-50">
                {data?.rtpay?.hasPin ? 'Change PIN' : 'Set PIN'}
              </button>
            )}
          </Field>

          {saveError && <p className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-[12px] text-red-800">{saveError}</p>}

          <div className="flex flex-wrap items-center justify-end gap-2 border-t border-stone-200/70 py-3">
            {dirty && <span className="mr-auto text-xs text-stone-500">Unsaved terminal settings</span>}
            <button type="button" disabled={!dirty || saving}
              onClick={() => { if (base) setDraft(base); setPin(''); setPinOpen(false); setMc(''); setMcOpen(false); setSaveError(null); }}
              className="min-h-[44px] rounded-lg px-3 text-[12px] font-semibold text-stone-600 hover:text-stone-900 disabled:opacity-40">
              Discard
            </button>
            <button type="button" disabled={!dirty || saving || disabled} onClick={save} data-testid="rtpay-save"
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg bg-stone-900 px-4 text-[12px] font-semibold text-white hover:bg-black disabled:opacity-40">
              {saving && <RubyGemMark size={13} drawing color="currentColor" />}
              {saving ? 'Saving…' : 'Save terminal settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default RubyTillPayDevicePanel;
